import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import axiosConfig from '../config/axiosConfig';

// ─── Fallback page catalog (backend available_pages is preferred) ──────────────
export const AVAILABLE_PAGES = [
  { key: 'dashboard',          label: 'Dashboard',                    group: 'core' },
  { key: 'contacts',           label: 'Contacts',                     group: 'whatsapp' },
  { key: 'inbox',              label: 'Inbox',                        group: 'whatsapp' },
  { key: 'campaigns',          label: 'WhatsApp Campaigns',           group: 'whatsapp' },
  { key: 'templates',          label: 'WhatsApp Templates',           group: 'whatsapp' },
  { key: 'wallet',             label: 'Wallet & Transactions',        group: 'billing' },
  { key: 'waba',               label: 'WABA Configuration',           group: 'settings' },
  { key: 'bot_flows',          label: 'Bot Flow Builder',             group: 'automation' },
  { key: 'ai_chatbot',         label: 'AI Chatbot',                   group: 'automation' },
  { key: 'knowledge_base',     label: 'Knowledge Base',               group: 'automation' },
  { key: 'email_dashboard',    label: 'Email Dashboard',              group: 'email' },
  { key: 'email_campaigns',    label: 'Email Campaigns',              group: 'email' },
  { key: 'email_templates',    label: 'Email Templates',              group: 'email' },
  { key: 'email_settings',     label: 'Email Settings',               group: 'email' },
  { key: 'email_sender',       label: 'Sender Profile & Domains',     group: 'email' },
  { key: 'api_keys',           label: 'API Keys & Developer Tools',   group: 'developer' },
  { key: 'settings',           label: 'Company Settings',             group: 'settings' },
  { key: 'admin',              label: 'Admin Management',             group: 'admin' },
  { key: 'platform_plans',     label: 'Platform Plans',               group: 'mos_admin' },
  { key: 'topup_plans',        label: 'Top-Up Plans',                 group: 'mos_admin' },
  { key: 'payment_management', label: 'Payment Management',           group: 'mos_admin' },
];

const PAGE_KEY_ALIASES = {
  analytics: 'dashboard',
  reports: 'dashboard',
  billing: 'wallet',
  team: 'admin',
  api_documentation: 'api_keys',
  api_docs: 'api_keys',
  'api-docs': 'api_keys',
  transactional: 'api_keys',
  transactional_messages: 'api_keys',
  'transactional-messages': 'api_keys',
  webhook: 'api_keys',
  webhooks: 'api_keys',
  audience_sources: 'api_keys',
  'audience-sources': 'api_keys',
  email_identity: 'email_sender',
  email_preferences: 'email_settings',
  'bot-flow': 'bot_flows',
  bot_flow: 'bot_flows',
  botflow: 'bot_flows',
};

const getEquivalentPageKeys = (page) => {
  if (!page) return [];
  const canonical = PAGE_KEY_ALIASES[page] || page;
  const aliases = Object.keys(PAGE_KEY_ALIASES).filter(key => PAGE_KEY_ALIASES[key] === canonical);
  return Array.from(new Set([page, canonical, ...aliases]));
};

// ─── Helper functions (can be used outside React components too) ───────────────

/** Does the user have access to a given page? null pages = super admin (all). */
export const hasPageAccess = (ctx, page) => {
  if (!ctx) return false;
  if (!ctx.pages) return true; // null = super admin → all pages
  if (Array.isArray(page)) return page.some(key => hasPageAccess(ctx, key));
  const pages = Array.isArray(ctx.pages) ? ctx.pages : [];
  return getEquivalentPageKeys(page).some(key => pages.includes(key));
};

/** Get the permission level for a page: 'read' | 'write' | null */
export const getPermission = (ctx, page) => {
  if (!ctx) return null;
  if (!ctx.permissions) return 'write'; // null = super admin → full access
  if (Array.isArray(page)) {
    return page.map(key => getPermission(ctx, key)).find(Boolean) || null;
  }
  return getEquivalentPageKeys(page).map(key => ctx.permissions[key]).find(Boolean) || null;
};

/** Can the user write (create/edit/delete) on this page? */
export const canWrite = (ctx, page) => {
  if (Array.isArray(page)) return page.some(key => canWrite(ctx, key));
  return getPermission(ctx, page) === 'write';
};

/** Can the user at least read this page? */
export const canRead = (ctx, page) => {
  if (Array.isArray(page)) return page.some(key => canRead(ctx, key));
  const perm = getPermission(ctx, page);
  return perm === 'read' || perm === 'write';
};

/** Is this user a child admin (has a parent)? */
export const isChildAdmin = (ctx) => {
  if (!ctx) return false;
  // Prefer explicit flag from API, fall back to checking parent_company_id
  if (ctx.is_child_admin !== undefined) return !!ctx.is_child_admin;
  return !!ctx.parent_company_id;
};

/** Is this user a super admin or top-level admin? */
export const isSuperAdmin = (ctx) => {
  if (!ctx) return false;
  if (ctx.is_child_admin) return false;
  // Prefer admin_level if available
  const adminLevel = ctx.admin_level || sessionStorage.getItem('admin_level');
  if (adminLevel) return adminLevel === 'mos_super_admin';
  // Fallback to legacy company_type check
  return !ctx.parent_company_id && (
    ctx.company_type === 'ADMIN' ||
    ctx.rolename === 'Super Admin' ||
    ctx.rolename === 'ADMIN'
  );
};

/**
 * Get the admin_level for the current user.
 * Hierarchy: mos_super_admin > mos_sub_admin > client_super_admin > client_sub_admin > null
 */
export const getAdminLevel = (ctx) => {
  if (!ctx) return sessionStorage.getItem('admin_level') || null;
  return ctx.admin_level || sessionStorage.getItem('admin_level') || null;
};

/** Is this a MOS platform admin (super or sub)? */
export const isMosAdmin = (ctx) => {
  const level = getAdminLevel(ctx);
  if (level) return level === 'mos_super_admin' || level === 'mos_sub_admin';
  // Fallback: company_type === 'ADMIN'
  const companyType = ctx?.company_type || sessionStorage.getItem('company_type');
  return companyType === 'ADMIN';
};

/** Is this a client admin (super or sub)? */
export const isClientAdmin = (ctx) => {
  const level = getAdminLevel(ctx);
  if (level) return level === 'client_super_admin' || level === 'client_sub_admin';
  // Fallback: DIRECT_CUSTOMER or SOLUTION_PARTNER
  const companyType = ctx?.company_type || sessionStorage.getItem('company_type');
  return companyType === 'DIRECT_CUSTOMER' || companyType === 'SOLUTION_PARTNER';
};

/** Is this a super-level admin (either MOS or client)? Has full access to their panel. */
export const isSuperLevel = (ctx) => {
  const level = getAdminLevel(ctx);
  if (level) return level === 'mos_super_admin' || level === 'client_super_admin';
  // Fallback: no parent + (ADMIN or super role name)
  if (!ctx) return false;
  return !ctx.parent_company_id && !ctx.is_child_admin;
};

/**
 * Get the correct API base path for admin operations.
 * MOS admins use /admin/*, client admins use /admin/client/*.
 */
export const getAdminApiBase = (ctx) => {
  if (isMosAdmin(ctx)) return '/admin';
  return '/admin/client';
};

/**
 * Get the effective company ID for API calls.
 * For child admins this is the parent's company ID.
 * For top-level admins/customers this is their own company ID.
 */
export const getEffectiveCompanyId = (ctx) => {
  if (!ctx) return sessionStorage.getItem('customeruuid') || null;
  return ctx.effective_company_id || ctx.own_company_id || sessionStorage.getItem('customeruuid') || null;
};

// ─── React Context ─────────────────────────────────────────────────────────────
const UserContext = createContext(null);

export const useUserContext = () => {
  const ctx = useContext(UserContext);
  if (!ctx) {
    // Return safe defaults when context is not available yet
    return {
      userCtx: null,
      loading: false,
      fetchUserContext: () => {},
      clearUserContext: () => {},
      availablePages: AVAILABLE_PAGES,
      rolePermissionPresets: {},
    };
  }
  return ctx;
};

export const UserContextProvider = ({ children }) => {
  const [userCtx, setUserCtx] = useState(() => {
    // Hydrate from sessionStorage on mount
    try {
      const stored = sessionStorage.getItem('user_context');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(false);

  // Fetch from GET /admin/user-context after login
  const fetchUserContext = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axiosConfig.get('/admin/user-context');
      if (response.data?.status && response.data?.statuscode === 200) {
        const entity = response.data.entity;
        setUserCtx(entity);
        // Persist in sessionStorage
        sessionStorage.setItem('user_context', JSON.stringify(entity));
        sessionStorage.setItem('rolename', entity.rolename || '');
        sessionStorage.setItem('pages', JSON.stringify(entity.pages));
        sessionStorage.setItem('permissions', JSON.stringify(entity.permissions));
        sessionStorage.setItem('parent_company_id', entity.parent_company_id || '');
        sessionStorage.setItem('own_company_id', entity.own_company_id || '');
        // Store admin_level if the backend provides it
        if (entity.admin_level) {
          sessionStorage.setItem('admin_level', entity.admin_level);
        }
        // Store available_pages from backend (single source of truth for page definitions)
        if (entity.available_pages) {
          sessionStorage.setItem('available_pages', JSON.stringify(entity.available_pages));
        } else {
          sessionStorage.removeItem('available_pages');
        }
        if (entity.role_permission_presets) {
          sessionStorage.setItem('role_permission_presets', JSON.stringify(entity.role_permission_presets));
        } else {
          sessionStorage.removeItem('role_permission_presets');
        }

        // For child admins, override customeruuid with the parent's effective_company_id
        // so all downstream API calls use the parent's data scope
        const effectiveId = entity.effective_company_id || entity.own_company_id || '';
        sessionStorage.setItem('effective_company_id', effectiveId);
        if ((entity.is_child_admin || entity.parent_company_id) && entity.effective_company_id) {
          sessionStorage.setItem('customeruuid', entity.effective_company_id);
        }

        return entity;
      }
    } catch (err) {
      console.error('Failed to fetch user context:', err);
    } finally {
      setLoading(false);
    }
    return null;
  }, []);

  // Clear on logout
  const clearUserContext = useCallback(() => {
    setUserCtx(null);
    sessionStorage.removeItem('user_context');
    sessionStorage.removeItem('rolename');
    sessionStorage.removeItem('pages');
    sessionStorage.removeItem('permissions');
    sessionStorage.removeItem('parent_company_id');
    sessionStorage.removeItem('own_company_id');
    sessionStorage.removeItem('effective_company_id');
    sessionStorage.removeItem('admin_level');
    sessionStorage.removeItem('available_pages');
    sessionStorage.removeItem('role_permission_presets');
  }, []);

  // Keep sessionStorage in sync whenever userCtx changes
  useEffect(() => {
    if (userCtx) {
      sessionStorage.setItem('user_context', JSON.stringify(userCtx));
    }
  }, [userCtx]);

  /**
   * Get available_pages — either from context (API response) or fallback to the
   * hardcoded AVAILABLE_PAGES constant. This is the single source of truth for
   * the permissions form and any page-key enumeration.
   */
  const availablePages = userCtx?.available_pages || (() => {
    try {
      const stored = sessionStorage.getItem('available_pages');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })() || AVAILABLE_PAGES;

  const rolePermissionPresets = userCtx?.role_permission_presets || (() => {
    try {
      const stored = sessionStorage.getItem('role_permission_presets');
      return stored ? JSON.parse(stored) : null;
    } catch {
      return null;
    }
  })() || {};

  return (
    <UserContext.Provider value={{ userCtx, loading, fetchUserContext, clearUserContext, availablePages, rolePermissionPresets }}>
      {children}
    </UserContext.Provider>
  );
};

export default UserContext;
